import { ref } from 'vue';
import type { AxiosError } from 'axios';
import {
  useMemoizedGet,
  useStrapiGet,
  useStrapiPost,
  useStrapiPut,
  clearMemoizedGetCache,
} from 'src/shared/composables/useStrapiApi';
import type { StrapiResponseError } from 'src/types/strapi/strapiResponse';

export interface SemesterOption {
  id: number;
  documentId: string;
  name: string;
  code: string;
  start_date?: string;
  end_date?: string;
}

export interface SchoolOption {
  id: number;
  documentId: string;
  full_name?: string;
  short_name?: string;
}

export interface SchoolClassSummary {
  id: number;
  documentId: string;
  name: string;
  code: string;
  semester?: {
    documentId?: string;
    name?: string;
    code?: string;
  };
  school?: {
    documentId?: string;
    full_name?: string;
    short_name?: string;
  };
  enrollments?: Array<{ id: number; documentId: string }>;
}

export interface SchoolClassDetail {
  id: number;
  documentId: string;
  name: string;
  code: string;
  school?: {
    documentId?: string;
    full_name?: string;
    short_name?: string;
  };
  semester?: {
    documentId?: string;
    name?: string;
    code?: string;
  };
  teachers?: Array<{
    documentId?: string;
    username?: string;
    email?: string;
    firstname?: string;
    lastname?: string;
  }>;
  // Every section assigned to the class (unlike assigned-sections, which caps at 12).
  assigned_sections?: Array<{
    id?: number;
    documentId?: string;
    section_name?: string;
  }>;
  enrollments?: Array<{
    documentId?: string;
    status?: EnrollmentStatus;
    enrollment_status?: EnrollmentStatus;
    student?: {
      documentId?: string;
      username?: string;
      email?: string;
      firstname?: string;
      lastname?: string;
      /** Last authenticated request (teacher/admin roster only). */
      lastActiveAt?: string | null;
    };
  }>;
}

export type EnrollmentStatus =
  'pending' | 'active' | 'dropped' | 'completed' | 'declined';

export interface EnrollmentRecord {
  id: number;
  documentId: string;
  enrollment_status?: EnrollmentStatus;
  status?: EnrollmentStatus;
  notes?: string;
}

/**
 * A parsed backend failure from the invite-link join endpoint. `code` is the
 * machine-readable Strapi `error.message` (e.g. 'ENROLLMENT_NOT_REOPENABLE');
 * `enrollmentStatus` carries the terminal status (declined/dropped/completed)
 * when the join was rejected because a prior enrollment can't be reopened.
 */
export interface JoinClassError {
  status: number;
  name: string;
  code: string;
  enrollmentStatus?: EnrollmentStatus;
}

export interface JoinClassResult {
  enrollment: EnrollmentRecord | null;
  error: JoinClassError | null;
}

export interface StudentProfileEnrollment {
  documentId: string;
  createdAt?: string;
  status?: EnrollmentStatus;
  enrollment_status?: EnrollmentStatus;
  semester?: {
    documentId?: string;
    name?: string;
    code?: string;
  };
  school_class?: {
    documentId?: string;
    name?: string;
    code?: string;
    school?: {
      documentId?: string;
      full_name?: string;
      short_name?: string;
    };
  };
}

export interface CourseOption {
  id: number;
  documentId: string;
  course_name: string;
  totalStudents?: number;
  assignedCount?: number;
  completedCount?: number;
}

export interface SectionOption {
  id: number;
  documentId: string;
  section_name: string;
  totalStudents?: number;
  assignedCount?: number;
  completedCount?: number;
}

export interface ClassAssignedCoursesResponse {
  courses: CourseOption[];
  totalCourses: number;
}

export interface ClassAssignedSectionsResponse {
  sections: SectionOption[];
  totalSections: number;
}

export interface ClassCourseAssignmentResult {
  classDocumentId: string;
  courseDocumentId: string;
  createdCount: number;
  existingCount: number;
  failedCount: number;
  totalEligibleStudents: number;
}

export interface ClassCourseProgressRow {
  // Render with `schoolUserDisplayName` — `username` holds the email for
  // self-registered accounts, so it is only a fallback.
  student?: {
    documentId?: string;
    username?: string;
    email?: string;
    firstname?: string;
    lastname?: string;
  };
  enrollmentStatus?: string;
  assigned?: boolean;
  userCourse?: {
    documentId?: string;
    score?: number;
    timeAccumulator?: number;
    startDate?: string;
    completionDate?: string;
    progress?: Record<string, unknown> | null;
  } | null;
}

export interface ClassSectionProgressRow {
  // See ClassCourseProgressRow — render with `schoolUserDisplayName`.
  student?: {
    documentId?: string;
    username?: string;
    email?: string;
    firstname?: string;
    lastname?: string;
  };
  enrollmentStatus?: string;
  assigned?: boolean;
  userSection?: {
    documentId?: string;
    score?: number;
    timeAccumulator?: number;
    startDate?: string;
    completionDate?: string;
    progress?: Record<string, unknown> | null;
  } | null;
}

export interface ClassCourseProgressResponse {
  classDocumentId: string;
  courseDocumentId: string;
  totalStudents: number;
  assignedCount: number;
  completedCount: number;
  rows: ClassCourseProgressRow[];
}

export interface ClassSectionProgressResponse {
  classDocumentId: string;
  sectionDocumentId: string;
  totalStudents: number;
  assignedCount: number;
  completedCount: number;
  rows: ClassSectionProgressRow[];
}

export interface StudentCourseAssignmentResult {
  classDocumentId: string;
  courseDocumentId: string;
  studentDocumentId: string;
  assigned: boolean;
  changed: boolean;
}

export interface BulkStudentCourseAssignmentResult {
  classDocumentId: string;
  courseDocumentId: string;
  assigned: boolean;
  requestedCount: number;
  changedCount: number;
  unchangedCount: number;
}

export interface StudentSectionAssignmentResult {
  classDocumentId: string;
  sectionDocumentId: string;
  studentDocumentId: string;
  assigned: boolean;
  changed: boolean;
}

export interface BulkStudentSectionAssignmentResult {
  classDocumentId: string;
  sectionDocumentId: string;
  assigned: boolean;
  requestedCount: number;
  changedCount: number;
  unchangedCount: number;
}

/** Normalize an Axios failure from the join endpoint into a `JoinClassError`. */
function parseJoinClassError(err: unknown): JoinClassError {
  const axiosErr = err as AxiosError<StrapiResponseError> | undefined;
  const strapiError = axiosErr?.response?.data?.error;
  const details: { status?: EnrollmentStatus } | undefined =
    strapiError?.details;
  return {
    status: strapiError?.status ?? axiosErr?.response?.status ?? 0,
    name: strapiError?.name ?? 'Error',
    code: strapiError?.message ?? '',
    ...(details?.status ? { enrollmentStatus: details.status } : {}),
  };
}

export function useSchoolData() {
  const loading = ref(false);

  /**
   * Enrolling changes which schools the backend considers the user a member of, so the
   * memoized school-scoped responses are stale even though the token is unchanged.
   */
  function invalidateSchoolContext() {
    clearMemoizedGetCache();
  }

  async function fetchSchools(): Promise<SchoolOption[]> {
    loading.value = true;
    try {
      const { data, error } = await useMemoizedGet(
        'schools',
        {
          sort: ['short_name:asc', 'full_name:asc'],
          fields: ['documentId', 'full_name', 'short_name'],
          'pagination[limit]': 200,
        },
        {
          suppressError: true,
        },
      );
      if (error.value) return [];
      return (data.value?.data || []) as SchoolOption[];
    } finally {
      loading.value = false;
    }
  }

  async function fetchSemesters(): Promise<SemesterOption[]> {
    const { execute, data, error } = useStrapiGet('semesters', {
      'pagination[limit]': 100,
      sort: ['start_date:desc'],
      fields: ['name', 'code', 'documentId', 'start_date', 'end_date'],
    });

    loading.value = true;
    try {
      await execute();
      if (error.value) return [];
      const rows = (data.value?.data || []) as SemesterOption[];
      return rows;
    } finally {
      loading.value = false;
    }
  }

  async function fetchClassesBySemester(
    semesterDocumentId: string,
    schoolDocumentId?: string,
  ): Promise<SchoolClassSummary[]> {
    if (!semesterDocumentId) return [];

    const { execute, data, error } = useStrapiGet(
      `school-classes/by-semester/${semesterDocumentId}`,
      {
        ...(schoolDocumentId ? { schoolDocumentId } : {}),
      },
    );

    loading.value = true;
    try {
      await execute();
      if (error.value) return [];
      return ((data.value?.data || []) as SchoolClassSummary[]).sort((a, b) =>
        (a?.name || '').localeCompare(b?.name || ''),
      );
    } finally {
      loading.value = false;
    }
  }

  async function fetchTeacherClasses(
    teacherDocumentId: string,
    semesterDocumentId?: string,
    schoolDocumentId?: string,
  ): Promise<SchoolClassSummary[]> {
    if (!teacherDocumentId) return [];

    const { execute, data, error } = useStrapiGet(
      `school-teachers/${teacherDocumentId}/classes`,
      {
        ...(semesterDocumentId ? { semesterDocumentId } : {}),
        ...(schoolDocumentId ? { schoolDocumentId } : {}),
      },
    );

    loading.value = true;
    try {
      await execute();
      if (error.value) return [];
      return (data.value?.data || []) as SchoolClassSummary[];
    } finally {
      loading.value = false;
    }
  }

  async function fetchStudentEnrollments(
    studentDocumentId: string,
    semesterDocumentId?: string,
    schoolDocumentId?: string,
  ): Promise<StudentProfileEnrollment[]> {
    if (!studentDocumentId) return [];

    const { execute, data, error } = useStrapiGet('school-enrollments', {
      studentDocumentId,
      ...(semesterDocumentId ? { semesterDocumentId } : {}),
      ...(schoolDocumentId ? { schoolDocumentId } : {}),
    });

    loading.value = true;
    try {
      await execute();
      if (error.value) return [];
      return (data.value?.data || []) as StudentProfileEnrollment[];
    } finally {
      loading.value = false;
    }
  }

  async function fetchStudentProfileBySemester(
    studentDocumentId: string,
    semesterDocumentId: string,
  ): Promise<StudentProfileEnrollment[]> {
    if (!studentDocumentId || !semesterDocumentId) return [];

    const { execute, data, error } = useStrapiGet(
      `school-students/${studentDocumentId}/profile/${semesterDocumentId}`,
    );

    loading.value = true;
    try {
      await execute();
      if (error.value) return [];
      return (data.value?.data || []) as StudentProfileEnrollment[];
    } finally {
      loading.value = false;
    }
  }

  async function fetchClassDetail(
    classDocumentId: string,
    semesterDocumentId?: string,
  ): Promise<SchoolClassDetail | null> {
    if (!classDocumentId) return null;

    const { execute, data, error } = useStrapiGet(
      `school-classes/${classDocumentId}/detail`,
      semesterDocumentId ? { semesterDocumentId } : {},
    );

    loading.value = true;
    try {
      await execute();
      if (error.value) return null;
      return (data.value?.data || null) as SchoolClassDetail | null;
    } finally {
      loading.value = false;
    }
  }

  async function fetchCoursesForAssignment(): Promise<CourseOption[]> {
    loading.value = true;
    try {
      const { data, error } = await useMemoizedGet(
        'school-courses',
        {},
        {
          suppressError: true,
        },
      );
      if (error.value) return [];
      return (data.value?.data || []) as CourseOption[];
    } finally {
      loading.value = false;
    }
  }

  async function fetchSectionsForAssignment(): Promise<SectionOption[]> {
    loading.value = true;
    try {
      const { data, error } = await useMemoizedGet(
        'school-sections',
        {},
        {
          suppressError: true,
        },
      );
      if (error.value) return [];
      return (data.value?.data || []) as SectionOption[];
    } finally {
      loading.value = false;
    }
  }

  async function assignCourseToClass(
    classDocumentId: string,
    courseDocumentId: string,
    semesterDocumentId?: string,
  ): Promise<ClassCourseAssignmentResult | null> {
    if (!classDocumentId || !courseDocumentId) return null;

    const { execute, data, error } = useStrapiPost(
      `school-classes/${classDocumentId}/assign-course`,
      {
        courseDocumentId,
        ...(semesterDocumentId ? { semesterDocumentId } : {}),
      },
    );

    loading.value = true;
    try {
      await execute();
      if (error.value) return null;
      return (data.value?.data || null) as ClassCourseAssignmentResult | null;
    } finally {
      loading.value = false;
    }
  }

  async function fetchClassAssignedCourses(
    classDocumentId: string,
    options?: {
      search?: string;
      limit?: number;
      // Scopes the per-course assigned/completed counters to the roster of this
      // semester. Without it the backend counts every student the class ever had.
      semesterDocumentId?: string;
    },
  ): Promise<ClassAssignedCoursesResponse> {
    if (!classDocumentId) {
      return {
        courses: [],
        totalCourses: 0,
      };
    }

    const { execute, data, error } = useStrapiGet(
      `school-classes/${classDocumentId}/assigned-courses`,
      {
        ...(options?.search ? { search: options.search } : {}),
        ...(options?.limit ? { limit: options.limit } : {}),
        ...(options?.semesterDocumentId
          ? { semesterDocumentId: options.semesterDocumentId }
          : {}),
      },
    );

    loading.value = true;
    try {
      await execute();
      if (error.value) {
        return {
          courses: [],
          totalCourses: 0,
        };
      }
      return {
        courses: (data.value?.data || []) as CourseOption[],
        totalCourses: Number(data.value?.meta?.totalCourses || 0),
      };
    } finally {
      loading.value = false;
    }
  }

  async function fetchClassCourseProgress(
    classDocumentId: string,
    courseDocumentId: string,
    semesterDocumentId?: string,
  ): Promise<ClassCourseProgressResponse | null> {
    if (!classDocumentId || !courseDocumentId) return null;

    const { execute, data, error } = useStrapiGet(
      `school-classes/${classDocumentId}/course-progress/${courseDocumentId}`,
      semesterDocumentId ? { semesterDocumentId } : {},
    );

    loading.value = true;
    try {
      await execute();
      if (error.value) return null;
      return (data.value?.data || null) as ClassCourseProgressResponse | null;
    } finally {
      loading.value = false;
    }
  }

  async function fetchClassAssignedSections(
    classDocumentId: string,
    options?: {
      search?: string;
      limit?: number;
      // See fetchClassAssignedCourses — counters follow the semester's roster.
      semesterDocumentId?: string;
    },
  ): Promise<ClassAssignedSectionsResponse> {
    if (!classDocumentId) {
      return {
        sections: [],
        totalSections: 0,
      };
    }

    const { execute, data, error } = useStrapiGet(
      `school-classes/${classDocumentId}/assigned-sections`,
      {
        ...(options?.search ? { search: options.search } : {}),
        ...(options?.limit ? { limit: options.limit } : {}),
        ...(options?.semesterDocumentId
          ? { semesterDocumentId: options.semesterDocumentId }
          : {}),
      },
    );

    loading.value = true;
    try {
      await execute();
      if (error.value) {
        return {
          sections: [],
          totalSections: 0,
        };
      }
      return {
        sections: (data.value?.data || []) as SectionOption[],
        totalSections: Number(data.value?.meta?.totalSections || 0),
      };
    } finally {
      loading.value = false;
    }
  }

  async function fetchClassSectionProgress(
    classDocumentId: string,
    sectionDocumentId: string,
    semesterDocumentId?: string,
  ): Promise<ClassSectionProgressResponse | null> {
    if (!classDocumentId || !sectionDocumentId) return null;

    const { execute, data, error } = useStrapiGet(
      `school-classes/${classDocumentId}/section-progress/${sectionDocumentId}`,
      semesterDocumentId ? { semesterDocumentId } : {},
    );

    loading.value = true;
    try {
      await execute();
      if (error.value) return null;
      return (data.value?.data || null) as ClassSectionProgressResponse | null;
    } finally {
      loading.value = false;
    }
  }

  async function setStudentCourseAssignment(
    classDocumentId: string,
    courseDocumentId: string,
    studentDocumentId: string,
    assigned: boolean,
  ): Promise<StudentCourseAssignmentResult | null> {
    if (!classDocumentId || !courseDocumentId || !studentDocumentId) {
      return null;
    }

    const { execute, data, error } = useStrapiPut(
      `school-classes/${classDocumentId}/course-students/${courseDocumentId}/assignment`,
      {
        studentDocumentId,
        assigned,
      },
    );

    loading.value = true;
    try {
      await execute();
      if (error.value) return null;
      return (data.value?.data || null) as StudentCourseAssignmentResult | null;
    } finally {
      loading.value = false;
    }
  }

  async function bulkSetStudentCourseAssignment(
    classDocumentId: string,
    courseDocumentId: string,
    studentDocumentIds: string[],
    assigned: boolean,
  ): Promise<BulkStudentCourseAssignmentResult | null> {
    if (!classDocumentId || !courseDocumentId) return null;

    const { execute, data, error } = useStrapiPost(
      `school-classes/${classDocumentId}/course-students/${courseDocumentId}/assignment-bulk`,
      {
        studentDocumentIds,
        assigned,
      },
    );

    loading.value = true;
    try {
      await execute();
      if (error.value) return null;
      return (data.value?.data ||
        null) as BulkStudentCourseAssignmentResult | null;
    } finally {
      loading.value = false;
    }
  }

  async function setStudentSectionAssignment(
    classDocumentId: string,
    sectionDocumentId: string,
    studentDocumentId: string,
    assigned: boolean,
  ): Promise<StudentSectionAssignmentResult | null> {
    if (!classDocumentId || !sectionDocumentId || !studentDocumentId) {
      return null;
    }

    const { execute, data, error } = useStrapiPut(
      `school-classes/${classDocumentId}/section-students/${sectionDocumentId}/assignment`,
      {
        studentDocumentId,
        assigned,
      },
    );

    loading.value = true;
    try {
      await execute();
      if (error.value) return null;
      return (data.value?.data ||
        null) as StudentSectionAssignmentResult | null;
    } finally {
      loading.value = false;
    }
  }

  async function bulkSetStudentSectionAssignment(
    classDocumentId: string,
    sectionDocumentId: string,
    studentDocumentIds: string[],
    assigned: boolean,
  ): Promise<BulkStudentSectionAssignmentResult | null> {
    if (!classDocumentId || !sectionDocumentId) return null;

    const { execute, data, error } = useStrapiPost(
      `school-classes/${classDocumentId}/section-students/${sectionDocumentId}/assignment-bulk`,
      {
        studentDocumentIds,
        assigned,
      },
    );

    loading.value = true;
    try {
      await execute();
      if (error.value) return null;
      return (data.value?.data ||
        null) as BulkStudentSectionAssignmentResult | null;
    } finally {
      loading.value = false;
    }
  }

  /**
   * Enrollment via a teacher's invite link → `pending`: the link is shareable, so it
   * still requires the teacher's approval. Passes the invite's `semesterDocumentId`
   * through so the student is enrolled into the semester the teacher shared (backend
   * rejects an ended semester).
   *
   * Returns a `{ enrollment, error }` result rather than surfacing a toast: the
   * generic error notification is suppressed so the join page can render a
   * dedicated, human-readable explanation (declined / removed / term ended / …).
   */
  async function joinClass(
    classDocumentId: string,
    semesterDocumentId?: string,
  ): Promise<JoinClassResult> {
    if (!classDocumentId) return { enrollment: null, error: null };

    const { execute, data, error } = useStrapiPost(
      'school-enrollments/join',
      {
        classDocumentId,
        ...(semesterDocumentId ? { semesterDocumentId } : {}),
      },
      { suppressError: true },
    );

    loading.value = true;
    try {
      // `execute()` REJECTS on a non-2xx response (vueuse's useAxios), so the
      // rejection has to be swallowed here — letting it escape skips the parsing
      // below, leaves the join page spinning forever and surfaces the raw
      // "Request failed with status code 409" via BaseLayout's onErrorCaptured.
      // The response is still on `error.value`, which is what we actually want.
      await execute().catch(() => null);
      if (error.value) {
        return { enrollment: null, error: parseJoinClassError(error.value) };
      }
      invalidateSchoolContext();
      return {
        enrollment: (data.value?.data || null) as EnrollmentRecord | null,
        error: null,
      };
    } finally {
      loading.value = false;
    }
  }

  /**
   * Teacher changes an enrollment's status:
   * approve (→ active) / decline (→ declined) a pending application, or drop (→ dropped)
   * an active student.
   */
  async function decideEnrollment(
    enrollmentDocumentId: string,
    decision: 'approve' | 'decline' | 'drop' | 'reinstate',
  ): Promise<EnrollmentRecord | null> {
    if (!enrollmentDocumentId) return null;

    const { execute, data, error } = useStrapiPut(
      `school-enrollments/${enrollmentDocumentId}/decision`,
      { decision },
    );

    loading.value = true;
    try {
      await execute();
      if (error.value) return null;
      return (data.value?.data || null) as EnrollmentRecord | null;
    } finally {
      loading.value = false;
    }
  }

  return {
    loading,
    fetchSchools,
    fetchSemesters,
    fetchClassesBySemester,
    fetchTeacherClasses,
    fetchStudentProfileBySemester,
    fetchStudentEnrollments,
    fetchClassDetail,
    fetchCoursesForAssignment,
    fetchSectionsForAssignment,
    fetchClassAssignedCourses,
    fetchClassAssignedSections,
    assignCourseToClass,
    fetchClassCourseProgress,
    fetchClassSectionProgress,
    setStudentCourseAssignment,
    bulkSetStudentCourseAssignment,
    setStudentSectionAssignment,
    bulkSetStudentSectionAssignment,
    joinClass,
    decideEnrollment,
  };
}
